import { useState } from 'react';
import { type Event } from '@/types/event';
import { formatDate } from '@/utils/date';
import { EventDetails } from '../EventDetails/EventDetails';
import clsx from 'clsx';
import styles from './EventCard.module.css';

interface EventCardProps {
  event: Event;
  isFocused: boolean;
  isExpanded: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClick: () => void;
}

/**
 * Individual event card with glitch effects
 * Visual degradation (blur, RGB chromatic aberration) when not focused
 */
export function EventCard({
  event,
  isFocused,
  isExpanded,
  onMouseEnter,
  onMouseLeave,
  onClick,
}: EventCardProps) {
  const [hasCopied, setHasCopied] = useState(false);

  const handleShare = async (e: React.MouseEvent) => {
    e.stopPropagation();

    const shareUrl = `${window.location.origin}#/event/${event.id}`;
    const shareData = {
      title: event.name,
      text: `Check out this event: ${event.name}`,
      url: shareUrl,
    };

    // Try Web Share API first (mobile native share sheet)
    if (navigator.share && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
        return;
      } catch {
        // User cancelled or error, fall through to clipboard
      }
    }

    // Fallback: copy to clipboard
    try {
      await navigator.clipboard.writeText(shareUrl);
      setHasCopied(true);
      setTimeout(() => setHasCopied(false), 2000);
    } catch {
      // Last resort: create temporary textarea for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = shareUrl;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setHasCopied(true);
      setTimeout(() => setHasCopied(false), 2000);
    }
  };

  return (
    <article
      className={clsx(
        styles.card,
        isFocused && styles.focused
      )}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      aria-expanded={isExpanded}
    >
      <div className={styles.summary}>
        <div className={styles.summaryLeft}>
          <p className={styles.date}>{formatDate(event.start)}</p>
          <h2 className={styles.name}>{event.name}</h2>
        </div>
        <div className={styles.summaryRight}>
          <a
            href={event.url}
            className={styles.ticketsLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            TICKETS
          </a>
        </div>
      </div>

      <EventDetails event={event} isOpen={isExpanded} onShare={handleShare} hasCopied={hasCopied} />
    </article>
  );
}
